import { useState, useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/joy';

// A native horizontal scrollbar sits wherever the scrollable content happens
// to end, which isn't always reachable or even visible (e.g. content shorter
// than the viewport, or a fixed bar covering the natural scrollbar position).
// This renders its own thumb, positioned wherever the caller needs it, and
// keeps it in sync with the real scroll container.
export function DetachedScrollbar({ scrollRef, bottom }) {
  const trackRef = useRef(null);
  const [thumb, setThumb] = useState({ left: 0, width: 0, visible: false });

  const updateThumb = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    if (scrollWidth <= clientWidth + 1) {
      setThumb((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }
    setThumb({
      left: (scrollLeft / scrollWidth) * 100,
      width: (clientWidth / scrollWidth) * 100,
      visible: true,
    });
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateThumb();
    el.addEventListener('scroll', updateThumb);
    const resizeObserver = new ResizeObserver(updateThumb);
    resizeObserver.observe(el);
    window.addEventListener('resize', updateThumb);
    return () => {
      el.removeEventListener('scroll', updateThumb);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateThumb);
    };
  }, [scrollRef, updateThumb]);

  const scrollToRatio = (ratio) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = ratio * el.scrollWidth - el.clientWidth / 2;
    updateThumb();
  };

  const handleTrackClick = (e) => {
    if (e.target !== trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    scrollToRatio((e.clientX - rect.left) / rect.width);
  };

  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    const el = scrollRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const startX = e.clientX;
    const startScrollLeft = el.scrollLeft;
    const trackWidth = track.getBoundingClientRect().width;

    const handleMouseMove = (moveEvent) => {
      const deltaRatio = (moveEvent.clientX - startX) / trackWidth;
      el.scrollLeft = startScrollLeft + deltaRatio * el.scrollWidth;
      updateThumb();
    };
    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  if (!thumb.visible) return null;

  return (
    <Box
      ref={trackRef}
      onClick={handleTrackClick}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom,
        zIndex: 900,
        px: { xs: 2, md: 3 },
        py: '6px',
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', height: '6px' }}>
        <Box
          onMouseDown={handleThumbMouseDown}
          sx={{
            position: 'absolute',
            top: 0,
            left: `${thumb.left}%`,
            width: `${thumb.width}%`,
            height: '100%',
            borderRadius: '999px',
            backgroundColor: 'var(--joy-palette-neutral-300)',
            cursor: 'grab',
            '&:hover': { backgroundColor: 'var(--joy-palette-neutral-400)' },
          }}
        />
      </Box>
    </Box>
  );
}
